import { useState, useEffect } from 'react';
import { User, CreditCard, Building2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function PortalSidebar({ client, fullName }) {
  const [stripeMethods, setStripeMethods] = useState([]);
  const token = localStorage.getItem('portal_token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => { loadMethods(); }, []);

  const loadMethods = () => {
    axios.get(`${API_URL}/api/portal/payment-methods`, { headers }).then(res => setStripeMethods(res.data.payment_methods || [])).catch(() => {});
  };

  const handleSaveMethod = async (type) => {
    try {
      const res = await axios.post(`${API_URL}/api/portal/save-payment-method`, { payment_method: type, origin_url: window.location.origin }, { headers });
      window.location.href = res.data.url;
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to start setup'); }
  };

  return (
    <div className="w-64 flex-shrink-0 space-y-4">
      <div className="bg-white rounded-xl border border-gray-200 p-6 text-center">
        <div className="w-20 h-20 bg-[#FFF3CD] rounded-full flex items-center justify-center mx-auto mb-4">
          <User className="w-10 h-10 text-[#F59E0B]" />
        </div>
        <h2 className="text-lg font-bold text-[#0A1628] uppercase">{fullName}</h2>
        {client.company_name && <p className="text-sm text-gray-600 font-medium">{client.company_name}</p>}
        {client.email && <p className="text-sm text-gray-500 mt-1">{client.email}</p>}
        {client.phone && <p className="text-sm text-gray-500">{client.phone}</p>}
        <span className={`inline-block mt-3 px-3 py-1 rounded-full text-xs font-semibold ${client.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
          {(client.status || 'active').toUpperCase()}
        </span>
      </div>
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="flex items-center gap-2 font-semibold text-gray-900"><CreditCard className="w-4 h-4 text-amber-500" /> Payment Methods</h3>
        </div>
        <MethodsList methods={stripeMethods} />
        <div className="flex gap-2 mt-3">
          <button onClick={() => handleSaveMethod('cc')} className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-[#014DB7] text-white text-xs font-medium rounded-lg hover:bg-[#003A8C]">
            <CreditCard className="w-3 h-3" /> {stripeMethods.length > 0 ? 'Replace Card' : 'Add Card'}
          </button>
          <button onClick={() => handleSaveMethod('ach')} className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-[#84CC16] text-white text-xs font-medium rounded-lg hover:bg-[#65a30d]">
            <Building2 className="w-3 h-3" /> {stripeMethods.length > 0 ? 'Replace Bank' : 'Add Bank'}
          </button>
        </div>
        <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-100">
          <p className="text-[11px] text-blue-800 font-medium mb-1">Replacement policy:</p>
          <p className="text-[10px] text-blue-600 leading-relaxed"><strong>Payment methods cannot be deleted.</strong> Add a new verified card or bank to replace the current one.</p>
          <p className="text-[10px] text-blue-600 leading-relaxed mt-1"><strong>Credit/Debit Card:</strong> Use the card button to verify a replacement with Stripe.</p>
          <p className="text-[10px] text-blue-600 leading-relaxed mt-1"><strong>ACH Bank Transfer:</strong> Use the bank button to verify a replacement through Stripe.</p>
        </div>
      </div>
    </div>
  );
}

function MethodsList({ methods }) {
  if (methods.length === 0) return (
    <div className="text-center py-3">
      <CreditCard className="w-8 h-8 mx-auto mb-2 text-gray-300" />
      <p className="text-sm text-gray-400">No payment methods saved</p>
    </div>
  );

  return (
    <div className="space-y-2">
      {methods.map(m => <MethodCard key={m.id} method={m} />)}
    </div>
  );
}

function MethodCard({ method }) {
  if (method.type === 'card') {
    return (
      <div className="p-3 rounded-lg border border-gray-200 bg-gray-50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className={`w-8 h-5 rounded flex items-center justify-center text-white text-[10px] font-bold ${method.brand === 'visa' ? 'bg-blue-600' : method.brand === 'mastercard' ? 'bg-red-500' : method.brand === 'amex' ? 'bg-blue-400' : 'bg-gray-500'}`}>
              {(method.brand || 'CARD').toUpperCase().slice(0, 4)}
            </div>
            <div>
              <p className="text-sm font-medium text-gray-900">**** {method.last4}</p>
              <p className="text-xs text-gray-500">Exp: {method.exp_month}/{method.exp_year}</p>
            </div>
          </div>
          <span className="text-[10px] text-blue-600 font-medium">Replace only</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-3 rounded-lg border border-green-200 bg-green-50">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-8 h-5 rounded bg-green-600 flex items-center justify-center text-white text-[10px] font-bold">ACH</div>
          <div>
            <p className="text-sm font-medium text-gray-900">{method.bank_name || 'Bank'} **** {method.last4}</p>
            <p className="text-xs text-gray-500">{method.account_type || 'checking'}</p>
          </div>
        </div>
        <span className="text-[10px] text-blue-600 font-medium">Replace only</span>
      </div>
    </div>
  );
}
